import React, { useEffect, useMemo, useState } from 'react';

import { Drawer, makeStyles, Select, TextField } from '@material-ui/core';
import { showConfirmCohortBaseResetModalAction } from 'actions/cohortBaseResetModal';
import { useDispatch, useSelector } from 'react-redux';
import { COHORT_RESET_MODAL_TYPES } from 'reducers/cohortBaseResetModal';
import { FunctionSelection } from 'screens/admin/Competencies/CompetencieByFunctionAndRole/CompetencyByFunctionAndRole';
import DateRangeSelectionDropdown from 'screens/analytics/DateRangeSelectionDropdown';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import { Button } from 'styles/admin';
import {
  Divider1,
  FlexContainer,
  GutterCSS,
  NewStyledIconButton,
  StyledBox,
  StyledButton,
  StyledGrid,
  StyledTooltip,
  StyledTypography,
  VerticallyAlignedFlexContainer,
  VerticallyCenterContainer,
} from 'styles/common';
import { checkRoleAccess } from 'utils/helpers/rolesAccess';
import * as yup from 'yup';

import { ANALYTICS_PREDEFINED_DATERANGES, lastQuarter } from 'constants/ProjectConstants';
import { ShareCohortIcon, StyledIcon, StyledCloseIcon, LockIcon } from 'constants/icons';
import { KH_LINKS } from 'constants/knowledgeHubLinks';
import theme from 'constants/theme';

import { ANALYTICS_COHORT_KEYS, ANALYTICS_COHORT_ROLES, createNewCohort } from 'services/analytics/review';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';

import TooltipInfo from 'uiToolkit/TooltipInfo';
import { Typography2, Typography5 } from 'uiToolkit/Typography';

import { ToggleSwitch } from '../../../components/ToggleSwitch';
import { ErrorMessage, Loader, NoData } from '../../analytics/commonComponents';
import ClickToSetVerticalNavBar from './ClickToSetVerticalNavBar/ClickToSetVerticalNavBar';
import CohortCreationEngagement from './CohortCreationEngagement';
import CohortCreationFeedback, { onClickPreCheck } from './CohortCreationFeedback';
import CohortCreationGoal from './CohortCreationGoal';
import { CohortCreationGrade } from './CohortCreationGrade';
import CohortCreationManager from './CohortCreationManager';
import CohortCreationOneOnOne from './CohortCreationOneOnOne';
import CohortCreationReview from './CohortCreationReview';
import CohortCreationRoles from './CohortCreationRoles';
import { useCohortCreationContext } from './CohortSidePanelContext';
import { useCohortCreationSidePanelControl } from './CohortSidePanelControlContext';
import { DropdownListElement, StyledDropdownListElement } from './CommonComponents';

const ANALYTICS_COHORT_KEY_TYPES = ['ENGAGEMENT', 'GOAL', 'REVIEW', 'ONE_ON_ONE', 'FEEDBACK'];
const OBJECT_TO_VALUES_KEYS = ['promotionStatus', 'reporteeMood'];
const PARSE_TO_INT_KEYS = ['reporteeMood'];
const SINGLE_SELECT_VALUES = ['reporteeRating'];
const OBJECT_TO_ARRAY_KEYS = ['competencies', 'engagementDrivers'];
const OBJECT_TO_ARRAY_KEYS_MULTIPLE = ['goalStatus'];
const DATA_RANGE_KEYS = ['data', 'reporteeRating'];
const OBJECT_VALUES_TO_ARRAY_KEYS = ['gridPositions'];

const analyticsDataSchema = yup.array(
  yup.object({
    minValue: yup.number().required(),
    maxValue: yup.number().required(),
  })
);
const analyticsDateRangeSchema = yup.object({
  endTS: yup.number().required(),
  startTS: yup.number().required(),
});

const analyticsSelectValueSchema = yup.array(
  yup.object({
    filterValue: yup.string().required(),
  })
);

const analyticsValidationSchema = {
  engagement: yup.object().shape({
    data: analyticsDataSchema.required(),
    engagementDrivers: analyticsSelectValueSchema.required(),
    source: yup.object().required(),
  }),
  feedback: yup.object().shape({
    data: analyticsDataSchema.required(),
    competencies: analyticsSelectValueSchema.required(),
    dateRange: analyticsDateRangeSchema.required(),
  }),
  goals: yup.object().shape(
    {
      dateRange: analyticsDateRangeSchema.required(),
      data: analyticsDataSchema.when('goalStatus', {
        is: (goalStatus) => !Array.isArray(goalStatus) || !goalStatus[0]?.filterValue,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema,
      }),
      goalStatus: analyticsSelectValueSchema.when('data', {
        is: (data) => !Array.isArray(data) || Number.isNaN(data[0]?.minValue) || Number.isNaN(data[0]?.maxValue),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema,
      }),
    },
    [
      ['data', 'goalStatus'],
      ['goalStatus', 'data'],
    ]
  ),
  oneOnOne: yup.object().shape(
    {
      dateRange: analyticsDateRangeSchema.required(),
      data: analyticsDataSchema.when(['reporteeMood', 'reporteeRating'], {
        is: (reporteeMood, reporteeRating) =>
          (!Array.isArray(reporteeMood) || !reporteeMood?.length) &&
          (!Array.isArray(reporteeRating) ||
            Number.isNaN(reporteeRating[0]?.minValue) ||
            Number.isNaN(reporteeRating[0]?.maxValue)),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema,
      }),
      reporteeMood: yup
        .array()
        .min(1)
        .when(['data', 'reporteeRating'], {
          is: (data, reporteeRating) =>
            (!Array.isArray(data) || Number.isNaN(data[0]?.minValue) || Number.isNaN(data[0]?.maxValue)) &&
            (!Array.isArray(reporteeRating) ||
              Number.isNaN(reporteeRating[0]?.minValue) ||
              Number.isNaN(reporteeRating[0]?.maxValue)),
          then: (schema) => schema.required(),
          otherwise: (schema) => schema,
        }),
      reporteeRating: analyticsDataSchema.when(['data', 'reporteeMood'], {
        is: (data, reporteeMood) =>
          (!Array.isArray(data) || Number.isNaN(data[0]?.minValue) || Number.isNaN(data[0]?.maxValue)) &&
          (!Array.isArray(reporteeMood) || !reporteeMood?.length),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema,
      }),
    },
    [
      ['data', 'reporteeMood'],
      ['data', 'reporteeRating'],
      ['reporteeMood', 'reporteeRating'],
    ] // explanation for this array => @ https://github.com/jquense/yup/issues/193#issuecomment-437878160
  ),
  review: yup.object().shape(
    {
      source: yup
        .object()
        .shape({
          displayName: yup.string(),
          enabled: yup.boolean(),
          keyName: yup.string(),
          uuid: yup.string().required(),
        })
        .required(),
      data: analyticsDataSchema.when(['promotionStatus', 'gridPositions'], {
        is: (promotionStatus, gridPositions) =>
          (!Array.isArray(promotionStatus) || !promotionStatus?.length) &&
          (!Array.isArray(gridPositions) || !gridPositions?.length),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema,
      }),
      promotionStatus: yup
        .array()
        .min(1)
        .when(['data', 'gridPositions'], {
          is: (data, gridPositions) =>
            (!Array.isArray(data) || Number.isNaN(data[0]?.minValue) || Number.isNaN(data[0]?.maxValue)) &&
            (!Array.isArray(gridPositions) || !gridPositions?.length),
          then: (schema) => schema.required(),
          otherwise: (schema) => schema,
        }),
      gridPositions: yup
        .array()
        .min(1)
        .when(['data', 'promotionStatus'], {
          is: (data, promotionStatus) =>
            (!Array.isArray(data) || Number.isNaN(data[0]?.minValue) || Number.isNaN(data[0]?.maxValue)) &&
            (!Array.isArray(promotionStatus) || !promotionStatus?.length),
          then: (schema) => schema.required(),
          otherwise: (schema) => schema,
        }),
    },
    [
      ['data', 'promotionStatus'],
      ['data', 'gridPositions'],
      ['promotionStatus', 'gridPositions'],
    ]
  ),
};

const filterOutQueryObject = async (queryObj) => {
  const queryObject = { ...queryObj };

  await Promise.all(
    ANALYTICS_COHORT_KEYS.map(async (analyticsKey) => {
      try {
        const isValid = await analyticsValidationSchema[analyticsKey].isValid(queryObject[analyticsKey]);
        if (!isValid) {
          delete queryObject[analyticsKey];
        }
      } catch (err) {
        console.error('Error in validating cohort schema : ', err);
      }
    })
  );

  return queryObject;
};

const cohortFiltersToQueryObjectHandler = async (selectedOptions) => {
  const queryObj = {
    ...selectedOptions,
  };

  // todo: do yup schema validation for the below code as well, to clean out the validation code

  Object.keys(selectedOptions).forEach((key) => {
    if (ANALYTICS_COHORT_KEYS.includes(key)) {
      Object.keys(selectedOptions[key]).forEach((k) => {
        if (DATA_RANGE_KEYS.includes(k)) {
          queryObj[key] = {
            ...queryObj[key],
            [k]: selectedOptions[key] ? (selectedOptions[key][k]?.isEnabled ? selectedOptions[key][k]?.range : []) : [],
          };
        }
      });

      Object.keys(queryObj[key]).forEach((k) => {
        if (PARSE_TO_INT_KEYS.includes(k)) {
          queryObj[key][k] = Object.keys(queryObj[key][k]).map((v) => +v);
        } else if (OBJECT_TO_VALUES_KEYS.includes(k)) {
          queryObj[key][k] = Object.keys(queryObj[key][k]);
        }
      });

      Object.keys(queryObj[key]).forEach((k) => {
        if (OBJECT_TO_ARRAY_KEYS.includes(k)) {
          queryObj[key][k] = [
            {
              filterValue: queryObj[key][k].key,
            },
          ];
        }
      });

      Object.keys(queryObj[key]).forEach((k) => {
        if (OBJECT_VALUES_TO_ARRAY_KEYS.includes(k)) {
          queryObj[key][k] = Object.values(queryObj[key][k]);
        }
      });

      Object.keys(queryObj[key]).forEach((k) => {
        if (OBJECT_TO_ARRAY_KEYS_MULTIPLE.includes(k)) {
          queryObj[key][k] = Object.values(queryObj[key][k]).map((v) => ({
            filterValue: v.key,
          }));
        }
      });
    } else if (selectedOptions[key]) {
      queryObj[key] = Object.values(selectedOptions[key]);
    }
  });

  const filteredQueryObject = await filterOutQueryObject(queryObj);
  return filteredQueryObject;
};

const StyledSelect = styled(Select)`
  .MuiSelect-root {
    padding-top: 0.375rem;
    padding-bottom: 0.375rem;
    padding-left: 1rem;
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) => props.theme.palette.text.blue};
  }
  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) => props.theme.palette.text.blue};
  }
  .MuiSelect-iconOutlined {
    fill: ${(props) => props.theme.palette.text.blue};
    margin-right: 0.15rem;
  }
  .MuiSelect-outlined {
    color: ${(props) => props.theme.palette.text.blue};
  }
`;

const useStyles = makeStyles({
  paper: {
    boxShadow: '3px 4px 5px 0px #0000001C',
    border: `1px solid ${theme.palette.borders.lighterGrey}`,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
});

export const DateRange = ({
  sectionId,
  onHeaderClick,
  goalTextLabel,
  accessibleRole,
  isAllEmployeesSelected,
  dateRange = lastQuarter,
  moduleName,
}) => {
  const [selectedDateRange, setSelectedDateRange] = useState(dateRange);

  const dispatch = useDispatch();

  const { updateBaseCohort } = useCohortCreationContext();
  const { enabledFeatures } = useSelector(selectLoggedInEmployeeData);

  useEffect(() => {
    if (sectionId) {
      onHeaderClick(sectionId, 'dateRange', {
        startTS: lastQuarter.startDate.valueOf(),
        endTS: lastQuarter.endDate.valueOf(),
      });
    }
  }, [sectionId]);

  const onHeaderClickCurried = (e) => () => {
    setSelectedDateRange(e);
    onHeaderClick(sectionId, 'dateRange', {
      startTS: e.startDate.valueOf(),
      endTS: e.endDate.valueOf(),
    });
  };

  return (
    <>
      <StyledVerticallyAlignedFlexContainer>
        <Typography2 style={{ marginRight: 'auto' }}>{goalTextLabel}</Typography2>
        <DateRangeSelectionDropdown
          defaultDateRange={lastQuarter}
          zIndex="1400 !important"
          disablePortal={false}
          predefinedRanges={ANALYTICS_PREDEFINED_DATERANGES}
          onDateRangeChange={(e) => {
            onClickPreCheck(
              dispatch,
              updateBaseCohort,
              onHeaderClickCurried(e),
              accessibleRole,
              isAllEmployeesSelected,
              moduleName,
              enabledFeatures
            );
          }}
          dateRange={selectedDateRange}
          customCalendarIcon
          iconSize="tinierFixed"
          buttonHeight="2.5rem"
          fontVariant="body2"
          buttonWidth="10rem"
        />
      </StyledVerticallyAlignedFlexContainer>
      <Divider1 topGutter={3} />
    </>
  );
};

const BASE_COHORTS = {
  ALL_EMPLOYEES: 'All employees',
  MY_ORGANIZATION: 'My Organization',
  MY_TEAM: 'My Team',
};

export const COHORT_COMP_TYPES = {
  // function: {
  //   type: 'function',
  //   component: () => <h5>Yes</h5>
  // },
  STATUS: {
    type: 'STATUS',
    component: ({ key: sectionId, displayName: sectionLabel, content }) => (
      <StatusComponent content={content} sectionLabel={sectionLabel} sectionId={sectionId} />
    ),
  },
  RATING: {
    type: 'RATING',
    component: ({ key: sectionId, displayName: sectionLabel, content }) => (
      <StatusComponent content={content} sectionLabel={sectionLabel} sectionId={sectionId} />
    ),
  },
  DEMOGRAPHICS: {
    type: 'DEMOGRAPHICS',
    component: ({ key: sectionId, displayName: sectionLabel, content }) => (
      <DemographicsComponent content={content} sectionLabel={sectionLabel} sectionId={sectionId} />
    ),
  },
  REVIEW: {
    type: 'REVIEW',
    component: ({ key: sectionId, displayName: sectionLabel, content, onHeaderClick, isAllEmployeesSelected }) => (
      <CohortCreationReview
        content={content}
        onHeaderClick={onHeaderClick}
        sectionLabel={sectionLabel}
        sectionId={sectionId}
        accessibleRole={ANALYTICS_COHORT_ROLES.review}
        isAllEmployeesSelected={isAllEmployeesSelected}
      />
    ),
  },
  ENGAGEMENT: {
    type: 'ENGAGEMENT',
    component: ({ key: sectionId, displayName: sectionLabel, content, onHeaderClick, isAllEmployeesSelected }) => (
      <CohortCreationEngagement
        content={content}
        onHeaderClick={onHeaderClick}
        sectionLabel={sectionLabel}
        sectionId={sectionId}
        accessibleRole={ANALYTICS_COHORT_ROLES.engagement}
        isAllEmployeesSelected={isAllEmployeesSelected}
      />
    ),
  },
  GOAL: {
    type: 'GOAL',
    component: ({ key: sectionId, displayName: sectionLabel, content, onHeaderClick, isAllEmployeesSelected }) => (
      <CohortCreationGoal
        content={content}
        onHeaderClick={onHeaderClick}
        sectionLabel={sectionLabel}
        sectionId={sectionId}
        accessibleRole={ANALYTICS_COHORT_ROLES.goals}
        isAllEmployeesSelected={isAllEmployeesSelected}
      />
    ),
  },
  ONE_ON_ONE: {
    type: 'ONE_ON_ONE',
    component: ({ key: sectionId, displayName: sectionLabel, content, onHeaderClick, isAllEmployeesSelected }) => (
      <CohortCreationOneOnOne
        content={content}
        onHeaderClick={onHeaderClick}
        sectionLabel={sectionLabel}
        sectionId={sectionId}
        accessibleRole={ANALYTICS_COHORT_ROLES.oneOnOne}
        isAllEmployeesSelected={isAllEmployeesSelected}
      />
    ),
  },
  FEEDBACK: {
    type: 'FEEDBACK',
    component: ({ key: sectionId, displayName: sectionLabel, content, onHeaderClick, isAllEmployeesSelected }) => (
      <CohortCreationFeedback
        content={content}
        onHeaderClick={onHeaderClick}
        sectionLabel={sectionLabel}
        sectionId={sectionId}
        accessibleRole={ANALYTICS_COHORT_ROLES.feedback}
        isAllEmployeesSelected={isAllEmployeesSelected}
      />
    ),
  },
  BASECOHORT: {
    type: 'BASECOHORT',
    component: ({ key: sectionId, displayName: sectionLabel, content }) => (
      <BaseCohortComponent content={content} sectionLabel={sectionLabel} sectionId={sectionId} />
    ),
  },
};

const NavHeader = ({
  isSelected,
  displayName,
  noOfSelectedValues = 0,
  isBaseCohort = false,
  noOfSelectedBaseCohort = 0,
}) => (
  <VerticallyCenterContainer style={{ justifyContent: 'space-between' }}>
    <StyledTypography variant="body2" color={isSelected ? theme.palette.text.primaryBlue : theme.palette.text.label}>
      {displayName}
    </StyledTypography>
    <CountWrapper background={isSelected ? theme.palette.text.primaryBlue : theme.palette.grey.main}>
      <StyledTypography variant="body2" color="white">
        {isBaseCohort ? noOfSelectedBaseCohort : noOfSelectedValues}
      </StyledTypography>
    </CountWrapper>
  </VerticallyCenterContainer>
);

// generalCohortCreation props is used to determine if we need to display only "All Employees" as baseCohort or not.
/**
 * @deprecated 09-01-2025 confirm before deletion: https://peoplemesh.slack.com/archives/C01AYG5JEAF/p1736420210454079
 */
const CohortSidepanel = ({
  defaultCohortVisibility = null,
  requiredCohortName = false,
  generalCohortCreation = false,
}) => {
  const dispatch = useDispatch();
  const { enabledFeatures } = useSelector(selectLoggedInEmployeeData);

  const {
    state: { open, onSuccess, onCancel, fetchData },
  } = useCohortCreationSidePanelControl();

  const {
    state: { name, selectedOptions, baseCohort: selectedBaseCohort, visibilityType },
    resetState,
    updateSelectedData,
  } = useCohortCreationContext();

  const [loading, setLoading] = useState(true);

  const [allSections, setAllSections] = useState(null);
  const [baseCohortSections, setBaseCohortSections] = useState(null);

  const [accessibleSections, setAccessibleSections] = useState(null);

  const canCreateOnlyOnAllEmployees =
    baseCohortSections?.content?.length === 1 && baseCohortSections.content[0]?.id === '-1';

  const handleKeyDown = ({ keyCode }) => {
    if (keyCode === 27) {
      resetState();
      onCancel();
    }
  };

  /**
   * @deprecated 06-08-2024 as per the usage of flag checkViewsEnabled.
   */
  const initialize = () => {
    setLoading(true);
    fetchData()
      .then((res) => {
        const { baseCohort, keys: cohortKeys, content } = res;

        const sectionsArr = cohortKeys
          .filter((keyObj) => content[keyObj.key])
          .map((keyObj) => ({
            ...keyObj,
            content: content[keyObj.key],
          }));

        if (baseCohort?.length > 0) {
          const baseCohortData = () => {
            if (generalCohortCreation) {
              return baseCohort.filter((cohort) => cohort?.uuid === '-1');
            }
            return baseCohort;
          };

          const baseCohortSection = {
            key: 'baseCohortSuperSet',
            cohortKeyType: 'BASECOHORT',
            displayName: 'Data Superset',
            content: baseCohortData().map(({ uuid, name }) => ({
              id: uuid,
              displayName: name,
            })),
          };
          setAllSections([baseCohortSection, ...sectionsArr]);
          setBaseCohortSections(baseCohortSection);
        } else {
          setAllSections(sectionsArr);
        }
      })
      .catch((err) => {
        console.error('Error in fetching cohort : ', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const saveCohort = async () => {
    try {
      setLoading(true);
      // creating query object.
      const queryObj = await cohortFiltersToQueryObjectHandler(selectedOptions);

      const res = await createNewCohort({
        cohortName: name,
        query: queryObj,
        baseCohortId: selectedBaseCohort?.uuid,
        visibilityType: defaultCohortVisibility || visibilityType,
      });
      onSuccess(res);
      resetState();
      dispatch(showSuccessSnackbar('Cohort successfully saved.'));
    } catch (err) {
      dispatch(showErrorSnackbar(err.message || 'Some error occurred while saving Cohort.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allSections?.length) {
      if (selectedBaseCohort.uuid === '-1') {
        const accessibleSectionsArray = allSections.filter((section) => {
          if (!generalCohortCreation && !canCreateOnlyOnAllEmployees) {
            return section;
          }
          if (!section.accessibleRole) {
            return section;
          }
          if (section.accessibleRole && checkRoleAccess(section.accessibleRole, enabledFeatures)) {
            return section;
          }
        });
        setAccessibleSections(accessibleSectionsArray);
      } else {
        setAccessibleSections(allSections);
      }
    }
  }, [allSections, selectedBaseCohort]);

  // sectionList for Clicktosetnavbar
  // the left side list

  const sectionList = accessibleSections?.map(({ key, cohortKeyType, displayName, content }, index) => {
    const selectedValues = selectedOptions[key] ? selectedOptions[key] : {};
    const noOfSelectedValues = ANALYTICS_COHORT_KEY_TYPES.includes(cohortKeyType)
      ? (selectedValues.data?.isEnabled ? 1 : 0) +
        [...OBJECT_TO_VALUES_KEYS, ...OBJECT_VALUES_TO_ARRAY_KEYS, ...OBJECT_TO_ARRAY_KEYS_MULTIPLE]
          .map((key) => (selectedValues[key] ? 1 : 0))
          .reduce((acc, curr) => acc + curr, 0) +
        SINGLE_SELECT_VALUES.map((key) => (selectedValues[key]?.isEnabled ? 1 : 0)).reduce((acc, curr) => acc + curr, 0)
      : (Object.keys(selectedValues)?.length ?? 0);
    const noOfSelectedBaseCohort = Object.keys(selectedBaseCohort).length;
    const onHeaderClick = (sectionId, type, obj) => updateSelectedData(sectionId, type, obj);
    return {
      id: key,
      header: (isSelected) => (
        <NavHeader
          noOfSelectedValues={noOfSelectedValues}
          isSelected={isSelected}
          displayName={displayName}
          isBaseCohort={key === 'baseCohortSuperSet'}
          noOfSelectedBaseCohort={noOfSelectedBaseCohort}
        />
      ),
      component: () => (
        <StyledBox>
          {COHORT_COMP_TYPES[cohortKeyType].component({
            content,
            displayName,
            key,
            onHeaderClick,
            isAllEmployeesSelected: selectedBaseCohort.uuid === '-1',
          })}
        </StyledBox>
      ),
    };
  });

  return (
    <StyledDrawer
      anchor="right"
      ModalProps={{
        onBackdropClick: () => {
          resetState();
          onCancel();
        },
      }}
      onKeyDown={handleKeyDown}
      open={open}
      onClose={() => {}}
    >
      <StyledBox height="100%" display="flex" flexDirection="column">
        {loading ? (
          <Loader />
        ) : (
          <CohortNameComponent
            requiredCohortName={requiredCohortName}
            defaultCohortVisibility={defaultCohortVisibility}
          />
        )}

        {loading ? (
          <Loader />
        ) : accessibleSections === null ? (
          <ErrorMessage errorText="Some error occurred while fetching data" />
        ) : accessibleSections.length === 0 ? (
          <NoData infoText="No Data to display" />
        ) : (
          <ClickToSetVerticalNavBar sectionList={sectionList} showDividers />
        )}

        <StyledBox
          borderTop={`1px solid ${theme.palette.borders.lightGrey}`}
          padding="1rem 1.5rem"
          marginTop="auto"
          flexcontainer
          alignitems="center"
          justifycontent="flex-end"
        >
          <StyledBox
            flexcontainer
            // alignitems="center"
            justifycontent="center"
            style={{ minHeight: '2.5rem' }}
          >
            <Button
              blueOutline
              size="large"
              onClick={() => {
                resetState(baseCohortSections?.content[0]?.id);
              }}
              style={{ width: '10rem', padding: '0.3rem 1.5rem' }}
            >
              <StyledTypography noWrap weight={600} variant="body2" color="primary">
                Clear Cohort
              </StyledTypography>
            </Button>
            <ButtonSpacer />
            <StyledTooltip title={requiredCohortName && !name ? 'Cohort name is mandatory' : ''}>
              <span>
                <Button
                  size="large"
                  blue
                  rightGutter={3}
                  onClick={saveCohort}
                  style={{ width: '10rem', height: '100%' }}
                  disabled={loading || (requiredCohortName && !name)}
                >
                  <StyledTypography weight={600} variant="body2" color="white">
                    Apply
                  </StyledTypography>
                </Button>
              </span>
            </StyledTooltip>
          </StyledBox>
        </StyledBox>
      </StyledBox>
    </StyledDrawer>
  );
};

const CohortNameComponent = ({ defaultCohortVisibility, requiredCohortName }) => {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const {
    state: { name: cohortName },
    setCohortName,
    resetState,
  } = useCohortCreationContext();

  const onChangeName = (value) => {
    setCohortName(value);
  };
  const {
    state: { onCancel },
  } = useCohortCreationSidePanelControl();

  return (
    <StyledBox padding="1.5rem">
      <VerticallyCenterContainer style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <FlexContainer style={{ flexDirection: 'column' }}>
          <VerticallyAlignedFlexContainer>
            <StyledTypography variant="body1">Create Cohort</StyledTypography>
            <TooltipInfo leftGutter={1} title={KH_LINKS.COHORTS.description} linkUrl={KH_LINKS.COHORTS.url} />
          </VerticallyAlignedFlexContainer>
          <StyledTypography variant="subtitle2" color={theme.palette.text.label} style={{ marginTop: '0.5rem' }}>
            Cohort helps you filter out employees and save for future use
          </StyledTypography>
        </FlexContainer>

        <NewStyledIconButton
          onClick={() => {
            resetState();
            onCancel();
          }}
        >
          <StyledCloseIcon color={theme.palette.icons.grey2} size="tiny" />
        </NewStyledIconButton>
      </VerticallyCenterContainer>
      <StyledGrid container alignItems="center" wrap="nowrap" style={{ marginTop: '1.5rem' }}>
        <StyledTextField
          rightGutter={2}
          variant="outlined"
          placeholder={
            requiredCohortName
              ? 'Enter cohort name to save it for future use *'
              : 'Enter cohort name to save it for future use '
          }
          value={cohortName}
          onChange={({ target }) => onChangeName(target.value)}
          onBlur={({ target }) => {
            onChangeName(target.value);
          }}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              onChangeName(ev.target.value);
            }
          }}
          margin="dense"
          style={{ marginTop: '0' }}
        />
        {loggedInEmployee?.canCreateSharedCohort && <CohortVisibilityComponent />}
      </StyledGrid>
    </StyledBox>
  );
};

const iconsList = [
  {
    id: 'ADMIN',
    icon: <ShareCohortIcon size="tiny" />,
    disabled: false,
    tooltipText: 'Shared Cohort',
  },
  {
    id: 'PRIVATE',
    icon: <LockIcon size="tinier" />,
    disabled: false,
    tooltipText: 'Private Cohort',
  },
];

const CohortVisibilityComponent = () => {
  const {
    state: {
      visibilityType: cohortVisibility,
      baseCohort: { uuid: baseCohortUuid },
    },
    updateCohortVisibility,
  } = useCohortCreationContext();

  const modifiedIconsList = useMemo(
    () =>
      iconsList.map((iconObj) => {
        if (iconObj.id === 'ADMIN') {
          if (String(baseCohortUuid) === '-1') {
            return {
              ...iconObj,
              tooltipText: 'Shared Cohort',
              disabled: false,
            };
          }

          return {
            ...iconObj,
            disabled: true,
            tooltipText: "Choose 'All employees' dataset to enable",
          };
        }
        return iconObj;
      }),
    [baseCohortUuid]
  );

  useEffect(() => {
    if (baseCohortUuid && String(baseCohortUuid) !== '-1') {
      updateCohortVisibility('PRIVATE');
    }
  }, [baseCohortUuid]);

  return (
    <ToggleSwitch
      selectedId={cohortVisibility}
      onIconClick={updateCohortVisibility}
      iconsList={modifiedIconsList}
      height="2.5rem"
      display="flex"
    />
  );
};

const StatusComponent = ({ sectionId, sectionLabel, content }) => {
  const {
    state: { selectedOptions },
    updateSelectedValue,
    deleteSelectedValue,
  } = useCohortCreationContext();

  const selectedValues = selectedOptions[sectionId] ? selectedOptions[sectionId] : {};

  return (
    <>
      <StyledTypography variant="body1" weight={600}>
        {sectionLabel}
      </StyledTypography>
      {Boolean(content) && content.length > 0 && (
        <StyledGrid container>
          {content.map((cObj) => {
            const { id: key, displayName: groupLabel, options } = cObj;
            return (
              <StyledGrid key={key} item container direction="column" xs={4} style={{ padding: '0 0.375rem' }}>
                <StyledTypography variant="body2" bottomGutter={1} truncate>
                  {groupLabel}
                </StyledTypography>
                {options.map((option) => {
                  const { id: optionId, displayName: optionLabel } = option;
                  const isSelected =
                    selectedValues[key] &&
                    selectedValues[key].options.filter((option) => option.id === optionId).length > 0;

                  return (
                    <StyledButton
                      key={optionId}
                      fullWidth
                      variant="outlined"
                      size="medium"
                      customColor={isSelected ? 'primary' : 'white'}
                      fontColor={isSelected ? 'white' : 'textSecondary'}
                      bottomGutter={1}
                      onClick={() => {
                        const valueObj = {
                          id: key,
                          displayName: groupLabel,
                          options: [option],
                        };

                        if (selectedValues[key]) deleteSelectedValue(sectionId, valueObj);
                        else updateSelectedValue(sectionId, valueObj);
                      }}
                    >
                      <StyledTypography variant="body1" truncate>
                        {optionLabel}
                      </StyledTypography>
                    </StyledButton>
                  );
                })}
              </StyledGrid>
            );
          })}
        </StyledGrid>
      )}
    </>
  );
};

const DemographicsComponent = ({ content, sectionLabel, sectionId }) => {
  const {
    state: { selectedOptions },
    appendSelectedValue,
    deleteSelectedValue,
  } = useCohortCreationContext();

  const selectedValues = selectedOptions[sectionId] ? selectedOptions[sectionId] : {};
  const noOfSelectedValues = Object.keys(selectedValues)?.length ?? 0;
  return (
    <>
      <HeadingContainer>
        <StyledTypography variant="body2" weight={600}>
          {sectionLabel}
        </StyledTypography>
        {noOfSelectedValues > 0 ? (
          <SelectedOptionsContainer>
            <Typography5 style={{ lineHeight: 1 }} customColor={theme.palette.text.white}>
              {noOfSelectedValues}
            </Typography5>
          </SelectedOptionsContainer>
        ) : null}
      </HeadingContainer>
      {sectionId === 'functionId' && (
        <FunctionSelection
          setSelectedFunction={({ uuid, name }) => {
            const cObj = { id: uuid, displayName: name };
            if (selectedValues[uuid]) deleteSelectedValue(sectionId, cObj);
            else appendSelectedValue(sectionId, cObj);
          }}
          isCohortCreation
          selectedFunctions={selectedOptions[sectionId]}
        />
      )}
      {sectionId === 'grade' && (
        <CohortCreationGrade
          setSelectedGrade={({ uuid, name }) => {
            const cObj = { id: uuid, displayName: name };
            if (selectedValues[uuid]) deleteSelectedValue(sectionId, cObj);
            else appendSelectedValue(sectionId, cObj);
          }}
          selectedValues={selectedOptions[sectionId]}
        />
      )}
      {sectionId === 'roleId' && <CohortCreationRoles sectionId={sectionId} />}
      {sectionId === 'managers' && <CohortCreationManager sectionId={sectionId} />}

      {sectionId !== 'roleId' && (
        <>
          {Boolean(content) && content.length > 0 && (
            <StyledGrid
              container
              // bottomGutter={2} topGutter={2}
            >
              {content.map((cObj) => {
                const { id, displayName: buttonLabel } = cObj;
                const isSelected = Boolean(selectedValues[id]);
                return (
                  <DropdownContainer>
                    <OptionWrapper
                      onClick={() => {
                        if (selectedValues[id]) deleteSelectedValue(sectionId, cObj);
                        else appendSelectedValue(sectionId, cObj);
                      }}
                    >
                      <StyledDropdownListElement
                        checked={isSelected}
                        radioColor={theme.palette.icons.blue}
                        hoverColor={theme.palette.icons.blue}
                        label={buttonLabel}
                      />
                    </OptionWrapper>
                  </DropdownContainer>
                );
              })}
            </StyledGrid>
          )}
        </>
      )}
    </>
  );
};

export const ExpandedView = ({ content, sectionLabel, sectionId, headerComponent, headingText }) => {
  const {
    state: { selectedOptions },
    updateSelectedData,
    deleteSelectedValue,
  } = useCohortCreationContext();

  const selectedValues = selectedOptions[sectionId] ? selectedOptions[sectionId] : {};
  const noOfSelectedValues =
    (selectedValues.data?.isEnabled ? 1 : 0) +
    [...OBJECT_TO_VALUES_KEYS, ...OBJECT_VALUES_TO_ARRAY_KEYS, ...OBJECT_TO_ARRAY_KEYS_MULTIPLE]
      .map((key) => (selectedValues[key] ? 1 : 0))
      .reduce((acc, curr) => acc + curr, 0) +
    SINGLE_SELECT_VALUES.map((key) => (selectedValues[key]?.isEnabled ? 1 : 0)).reduce((acc, curr) => acc + curr, 0);

  return (
    <>
      <HeadingContainer>
        <StyledTypography variant="body2" weight={600}>
          {sectionLabel}
        </StyledTypography>
        {noOfSelectedValues > 0 ? (
          <SelectedOptionsContainer>
            <Typography5 style={{ lineHeight: 1 }} customColor={theme.palette.text.white}>
              {noOfSelectedValues}
            </Typography5>
          </SelectedOptionsContainer>
        ) : null}
      </HeadingContainer>
      {Boolean(content) && content.length > 0 && (
        <StyledGrid
          container
          // bottomGutter={2} topGutter={2}
        >
          <DropdownContainer>{headerComponent && headerComponent()}</DropdownContainer>
        </StyledGrid>
      )}
    </>
  );
};

const BaseCohortComponent = ({ content, sectionLabel, sectionId }) => {
  const {
    state: { baseCohort: selectedBaseCohort, selectedOptions },
    updateBaseCohort,
    deleteSelectedKeyValue,
  } = useCohortCreationContext();

  const noOfSelectedValues = Object.keys(selectedBaseCohort).length;

  useEffect(() => {
    if (!selectedBaseCohort.uuid) {
      updateBaseCohort(content?.[0]?.id);
    }
  }, [selectedBaseCohort]);
  const dispatch = useDispatch();
  const { enabledFeatures } = useSelector(selectLoggedInEmployeeData);

  // probably will have to move this  function outside

  const checkIfAnyNonAdminFiltersAreSelected = () => {
    const selectedOptionsKeys = Object.keys(selectedOptions);
    const selectedOptionsAdminRoles = selectedOptionsKeys.map((k) => ({
      adminKey: ANALYTICS_COHORT_ROLES[k],
      filterKey: k,
    }));

    const keysForWhichUserLacksAccess = [];

    selectedOptionsAdminRoles.forEach((key) => {
      if (checkRoleAccess(key.adminKey, enabledFeatures)) {
      } else {
        keysForWhichUserLacksAccess.push(key);
      }
    });
    return keysForWhichUserLacksAccess;
  };

  const selectedFiltersForWhichUserLacksAccess = (arrayOfKeysForWhichUserLacksAccess) => {
    arrayOfKeysForWhichUserLacksAccess.forEach((k) => {
      deleteSelectedKeyValue(k.filterKey);
    });
  };

  return (
    <>
      <HeadingContainer>
        <StyledTypography variant="body2" weight={600}>
          {sectionLabel}
        </StyledTypography>
        {noOfSelectedValues > 0 ? (
          <SelectedOptionsContainer>
            <Typography5 style={{ lineHeight: 1 }} customColor={theme.palette.text.white}>
              {noOfSelectedValues}
            </Typography5>
          </SelectedOptionsContainer>
        ) : null}
      </HeadingContainer>
      {Boolean(content) && content.length > 0 && (
        <StyledGrid
          container
          // bottomGutter={2} topGutter={2}
        >
          {content.map((cObj) => {
            const { id, displayName: buttonLabel } = cObj;
            const isSelected = id === selectedBaseCohort?.uuid;
            return (
              <DropdownContainer>
                <OptionWrapper
                  onClick={() => {
                    // need to check if there are any filters applied
                    // for which user isn't an ADMIN
                    // if user is then show a confirmation modal that those filters will be cleared.
                    if (id === '-1') {
                      const arrayForWhichAccessLacks = checkIfAnyNonAdminFiltersAreSelected();
                      if (arrayForWhichAccessLacks.length) {
                        // open a confirmation modal, if user click yes then go ahead
                        // with setting base as "My org" and then normal flow
                        dispatch(
                          showConfirmCohortBaseResetModalAction(
                            () => {
                              selectedFiltersForWhichUserLacksAccess(arrayForWhichAccessLacks);
                              updateBaseCohort(id);

                              // updateBaseCohort('-3');
                              // callBackFunc();
                            },
                            () => {
                              console.log('');
                            },
                            COHORT_RESET_MODAL_TYPES.setToAllEmployeesModal
                          )
                        );

                        return true;
                      }
                    }
                    updateBaseCohort(id);
                  }}
                >
                  <DropdownListElement
                    checked={isSelected}
                    radioColor={theme.palette.icons.blue}
                    hoverColor={theme.palette.icons.blue}
                    label={buttonLabel}
                  />
                </OptionWrapper>
              </DropdownContainer>
            );
          })}
        </StyledGrid>
      )}
    </>
  );
};

const StyledDrawer = styled(Drawer)`
  ${(props) => props.theme.breakpoints.up('sm')} {
    .MuiDrawer-paper {
      width: 40%;
    }
  }
`;

const StyledTextField = styled(TextField)`
  flex: 1;
  ${(props) =>
    props.height &&
    css`
      height: ${props.height};
    `}
  ${GutterCSS};
  margin-bottom: 0;
  & .MuiInputBase-root {
    height: 100%;
  }
  & .MuiInputBase-root .MuiInputBase-input {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
  & .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) => props.theme.palette.borders.lighterGrey} !important;
  }
`;

const CountWrapper = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  background: ${(props) => props.background};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SelectedOptionsContainer = styled.div`
  height: 26px;
  width: 26px;
  background-color: ${theme.palette.background.blue.dark.v1};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 9px;
`;

const OptionWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
`;

const HeadingContainer = styled.div`
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 4rem;
`;

const DropdownContainer = styled.div`
  padding: 0.5rem 1.5rem;
  width: 100%;
  ${GutterCSS};
`;

const ButtonSpacer = styled.div`
  display: inline-block;
  width: 1rem;
`;

const StyledVerticallyAlignedFlexContainer = styled(VerticallyAlignedFlexContainer)`
  width: 100%;
`;

const CircularIcon = styled(StyledIcon)`
  border-radius: 50%;
  border: 1px solid #eeeeee;
  padding: 1rem;
  ${(props) =>
    props.backgroundColor &&
    css`
      background-color: ${props.backgroundColor};
    `};
`;

export default CohortSidepanel;
